import { DataResult, State } from "@progress/kendo-data-query";
import { RefObject } from "react";
import { MenuItem } from "@mui/material";
import axios from "axios";
import { faBook, faClipboard, faObjectUngroup } from "@fortawesome/free-solid-svg-icons";
import { ToolbarDropdownMenu } from "../../Grid/Toolbar/ToolbarDropdownMenu";
import ExcelExportButton from "../../Grid/ExcelExport/ExcelExportButton";
import { FtlRoute } from "./FtlRoute";
import { controlDepartmentAsString } from "../../Project/ViewModel/ControlDepartment";
import { routeStateAsString } from "../../Project/ViewModel/RouteState";
import { UppercaseButton } from "../../Layout/Buttons/UppercaseButton";
import { combinedUrls } from "../../URL/functions";
import { ftlRouteUrls } from "../../URL/ApplicationUrls";
import { KENDO_BUTTON_CLASSES } from "../../Layout/Buttons/KendoButtons";
import { IconWithRightMargin } from "../../Layout/Icons/IconWithRightMargin";
import { useExportWithToast } from "../../Grid/ExcelExport/useExportWithToast";
import { defaultDateFormatDDMMYYYYSeperatedByPeriod, formatDateTo } from "../../Utils/dates";
import { getFtlRouteIds } from "../../Grid/gridUtils";
import { isArrayUndefinedOrEmpty } from "../../Utils/arrays";

type Props = {
  dataState: State;
  ref: RefObject<any>;
  selectedIds: number[];
  tableDataResult: DataResult;
  allItemsSelected: boolean;
};

export const ftlRouteOverviewToolbarMenu = (url: string) =>
  function FtlRouteExportMenu({
    dataState,
    ref: { current },
    selectedIds,
    tableDataResult,
    allItemsSelected,
  }: Props) {
    const exportRouteCatalogue = useExportWithToast(
      () =>
        axios.get(combinedUrls(ftlRouteUrls.base.apiUrl, "RouteCatalogue"), {
          responseType: "blob",
        }),
      "Exporting route catalogue"
    );

    const exportRas = useExportWithToast(
      (routeId: number | undefined) =>
        axios.get(combinedUrls(ftlRouteUrls.base.apiUrl, `${routeId}/ExportRas`), {
          responseType: "blob",
        }),
      "Exporting RAS"
    );

    // Export FTL BidPack
    const exportFtlBidPack = useExportWithToast(
      (routeIds: number[] | undefined) =>
        axios.post(combinedUrls(ftlRouteUrls.base.apiUrl, "BidPack"), routeIds, {
          responseType: "blob",
        }),
      "Exporting FTL bid pack"
    );

    const exportFtlMasterList = useExportWithToast(
      () =>
        axios.get(combinedUrls(ftlRouteUrls.base.apiUrl, "Masterlist"), {
          responseType: "blob",
        }),
      "Exporting FTL master list"
    );

    return (
      <ToolbarDropdownMenu text="Exports" key="ftl-routes-export-menu">
        <MenuItem>
          <ExcelExportButton
            buttonText="Export Current Grid"
            url={url}
            dataState={dataState}
            columns={current?.columns.slice(1)}
            fileName="FilteredFtlRoutes"
            exportMapping={(ftlRoute: FtlRoute) => ({
              ...ftlRoute,
              LaunchClassification: controlDepartmentAsString.get(ftlRoute.LaunchClassification),
              Status: routeStateAsString.get(ftlRoute.Status),
              LaunchDate: formatDateTo(
                ftlRoute.LaunchDate,
                defaultDateFormatDDMMYYYYSeperatedByPeriod
              ),
            })}
          />
        </MenuItem>
        <MenuItem>
          <UppercaseButton className={KENDO_BUTTON_CLASSES} onClick={() => exportRouteCatalogue()}>
            <IconWithRightMargin icon={faClipboard} />
            Export Route Catalogue
          </UppercaseButton>
        </MenuItem>
        <MenuItem>
          <UppercaseButton
            className={KENDO_BUTTON_CLASSES}
            onClick={async () => {
              if (selectedIds.length !== 1) return;
              const selectedFtlRouteIds: number[] = await getFtlRouteIds({
                selectedIds,
                currentDatastate: dataState,
                tableDataResult,
                allItemsSelected,
              });
              if (isArrayUndefinedOrEmpty(selectedFtlRouteIds)) return;
              await exportRas(selectedFtlRouteIds[0]);
            }}
            disabled={selectedIds.length !== 1}
          >
            <IconWithRightMargin icon={faBook} />
            Export RAS
          </UppercaseButton>
        </MenuItem>
        <MenuItem>
          <UppercaseButton
            className={KENDO_BUTTON_CLASSES}
            onClick={async () => {
              const selectedFtlRouteIds: number[] = await getFtlRouteIds({
                selectedIds,
                currentDatastate: dataState,
                tableDataResult,
                allItemsSelected,
              });
              if (isArrayUndefinedOrEmpty(selectedFtlRouteIds)) return;
              await exportFtlBidPack(selectedFtlRouteIds);
            }}
            disabled={selectedIds.length === 0}
          >
            <IconWithRightMargin icon={faObjectUngroup} />
            Export FTL BidPack
          </UppercaseButton>
        </MenuItem>
        <MenuItem>
          <UppercaseButton className={KENDO_BUTTON_CLASSES} onClick={() => exportFtlMasterList()}>
            <IconWithRightMargin icon={faBook} />
            Export FTL Masterlist
          </UppercaseButton>
        </MenuItem>
      </ToolbarDropdownMenu>
    );
  };
